import { useEffect, useState } from 'react';
import { ScrollMode, Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { PdfViewerWrapper } from '@styles/InputStyles';
import { base64ToUint8Array } from '../commonFunctions';

interface IPdfViewerProps {
  fileSource?: string;
  scrollMode?: ScrollMode;
}

const isUrl = (str: string): boolean => {
  try {
    return str.startsWith('http://') || str.startsWith('https://');
  } catch {
    return false;
  }
};

const stringToBase64 = (str: string): string => {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
};
const isBase64 = (str: string): boolean => {
  if (typeof str !== 'string') return false;
  return /^[A-Za-z0-9+/=]+$/.test(str.replace(/\s+/g, ''));
};

export const PdfViewer = ({ fileSource: fileSource = '', scrollMode = ScrollMode.Vertical }: IPdfViewerProps) => {
  try {
    const [renderSource, setRenderSource] = useState<string | Uint8Array>('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (!fileSource) {
        setRenderSource('');
        return;
      }

      if (isUrl(fileSource)) {
        setLoading(true);
        // We manually fetch to force the 'Origin' header (fixing the 0-byte issue)
        fetch(fileSource, {
          method: 'GET',
          mode: 'cors',
        })
          .then((res) => {
            if (!res.ok) throw new Error('CORS or Network error');
            return res.arrayBuffer();
          })
          .then((buffer) => {
            setRenderSource(new Uint8Array(buffer));
            setLoading(false);
          })
          .catch((err) => {
            console.error('PDF Fetch Error:', err);
            // If URL fetch fails, we don't clear it immediately in case
            // the viewer can handle it as a fallback, but usually, it ends here.
            setLoading(false);
          });
      } else {
        // 2. Handle as Base64
        try {
          const safeBase64 = isBase64(fileSource) ? fileSource : stringToBase64(fileSource);
          setRenderSource(base64ToUint8Array(safeBase64));
        } catch (e) {
          console.error('Invalid Base64', e);
        }
      }
    }, [fileSource]);

    const defaultLayoutPluginInstance = defaultLayoutPlugin();

    if (loading) return <div>Loading PDF Document...</div>;
    if (!renderSource) return null;

    return (
      <PdfViewerWrapper>
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.13.216/build/pdf.worker.min.js">
          {fileSource && (
            <Viewer
              fileUrl={renderSource}
              plugins={[defaultLayoutPluginInstance]}
              scrollMode={scrollMode}
            />
          )}
        </Worker>
      </PdfViewerWrapper>
    );
  } catch (error) {
    console.warn('Invalid Base64 input, cannot decode:', error);
    return null;
  }
};
